/**
 * Hook to trigger a callback when the tab becomes visible again after being
 * hidden for at least `minHiddenMs` milliseconds.
 *
 * Solves the common problem where Supabase Realtime channels and gateway
 * WebSocket connections become stale while the browser tab is in the
 * background (browsers throttle timers and can close idle sockets).
 */

import { useEffect, useRef } from 'react';

interface UseVisibilityRefreshOptions {
  /** Callback to run when the tab regains focus after being hidden. */
  onVisible: () => void;
  /** Minimum time (ms) the tab must have been hidden before triggering. Default 5 000 ms. */
  minHiddenMs?: number;
  /** Whether the hook is active. Default true. */
  enabled?: boolean;
}

export function useVisibilityRefresh({
  onVisible,
  minHiddenMs = 5_000,
  enabled = true,
}: UseVisibilityRefreshOptions) {
  const hiddenAtRef = useRef(0);
  const callbackRef = useRef(onVisible);
  callbackRef.current = onVisible;

  useEffect(() => {
    if (!enabled) return;

    function handle() {
      if (document.visibilityState === 'hidden') {
        hiddenAtRef.current = Date.now();
        return;
      }
      // Tab is visible again
      const hiddenMs = Date.now() - hiddenAtRef.current;
      if (hiddenAtRef.current > 0 && hiddenMs >= minHiddenMs) {
        callbackRef.current();
      }
    }

    document.addEventListener('visibilitychange', handle);
    return () => document.removeEventListener('visibilitychange', handle);
  }, [enabled, minHiddenMs]);
}
